import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const config = JSON.parse(document.getElementById('fh-gift-config').textContent);
const root = window.Shopify?.routes?.root || '/';
let queue = Promise.resolve();
const pending = new Set();
let checkingOut = false;
let jobs = 0;
function busy() { document.documentElement.toggleAttribute('data-fh-gift-busy', jobs > 0 || pending.size > 0); }

async function request(path, body) {
  const response = await fetch(`${root}cart${path}.js`, {
    method: body ? 'POST' : 'GET', credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    ...(body ? { body: JSON.stringify(body) } : { cache: 'no-store' }),
  });
  const data = await response.json();
  if (!response.ok || data.status) throw new Error(data.description || config.error);
  return data;
}

function enqueue(task) {
  jobs++; busy();
  const work = queue.then(task, task).finally(() => { jobs--; busy(); });
  queue = work.catch(() => {});
  return work;
}

function showError(error) {
  console.error('FH gift:', error);
  const status = document.getElementById('fh-gift-status');
  status.textContent = config.error;
  status.hidden = false;
}

async function syncGift() {
  let cart = await request('');
  const eligible = cart.items.some(item => config.products.includes(item.product_id));
  const gifts = cart.items.filter(item => item.variant_id === config.variant);
  const desired = eligible && config.available ? 1 : 0;
  const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map(node => node.dataset.sectionId).filter(Boolean))].slice(0, 5);
  const rendering = { sections, sections_url: location.pathname };
  let result;
  if (desired && gifts.length === 0) {
    result = await request('/add', { items: [{ id: config.variant, quantity: 1 }], ...rendering });
  } else {
    const updates = {};
    gifts.forEach((item, index) => {
      const quantity = index === 0 ? desired : 0;
      if (item.quantity !== quantity) updates[item.key] = quantity;
    });
    if (Object.keys(updates).length) result = await request('/update', { updates, ...rendering });
  }
  document.getElementById('fh-gift-status').hidden = true;
  if (!result) return null;
  cart = await request('');
  return {
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: { items: cart.items, sections: result.sections, didError: false },
  };
}

// Wrap the native update promise before the drawer and cart components receive it.
// Their usual render therefore uses the final cart, including its one free gift.
window.addEventListener(StandardEvents.cartLinesUpdate, event => {
  if (event.fhGift || !event.promise) return;
  const work = event.promise.then(async result => {
    if (result.detail?.didError) return result;
    try {
      const updated = await enqueue(syncGift);
      if (updated && result.detail?.source === 'cart-items-component') broadcast(updated);
      return updated ? { ...result, cart: { ...updated.cart, id: result.cart?.id || updated.cart.id }, detail: { ...result.detail, ...updated.detail } } : result;
    } catch (error) {
      showError(error);
      return result; // Preserve successful paid-item additions if the gift service fails.
    }
  });
  event.promise = work;
  pending.add(work); busy();
  const done = () => { pending.delete(work); busy(); };
  work.then(done, done);
}, true);

function broadcast(result) {
  const event = new CartLinesUpdateEvent({
    action: 'update', context: 'cart', lines: [],
    promise: Promise.resolve({ ...result, detail: { ...result.detail, source: 'fh-gift' } }),
  });
  event.fhGift = true;
  (document.querySelector('theme-drawer#cart-drawer') || document).dispatchEvent(event);
}

async function reconcile() {
  const result = await enqueue(syncGift);
  if (result) broadcast(result);
}

// Normal checkout waits for additions and gift reconciliation to finish.
document.addEventListener('submit', async event => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || event.submitter?.name !== 'checkout' || checkingOut) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  const button = event.submitter;
  button.disabled = true;
  try {
    while (pending.size) await Promise.allSettled([...pending]);
    await reconcile();
    checkingOut = true;
    button.disabled = false;
    // Rendering may have replaced the checkout form and its button.
    const currentButton = document.querySelector('button[name="checkout"]');
    if (currentButton?.form) currentButton.form.requestSubmit(currentButton);
    else { checkingOut = false; throw new Error('Checkout form unavailable'); }
  } catch (error) { showError(error); button.disabled = false; }
}, true);

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => reconcile().catch(showError), { once: true });
else reconcile().catch(showError);
window.addEventListener('pageshow', event => { if (event.persisted) reconcile().catch(showError); });
